import { notifications } from '@mantine/notifications';
import { createUserWithEmailAndPassword, EmailAuthProvider, GoogleAuthProvider, linkWithCredential, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup, updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useState } from 'react';
import { auth, db, googleProvider } from '../../servicos/firebase';
import './Acesso.css';

const Acesso = ({ onLogin, onClose, initialView = 'login' }) => {
    const [view, setView] = useState(initialView);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [registeredName, setRegisteredName] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [pendingCred, setPendingCred] = useState(null); // Para vincular contas

    const showAlert = (message, type = 'info') => {
        notifications.show({
            title: type === 'danger' ? 'Erro' : (type === 'warning' ? 'Atenção' : 'Sucesso'),
            message,
            color: type === 'danger' ? 'red' : (type === 'warning' ? 'orange' : 'teal'),
            autoClose: 5000,
        });
    };

    const formatPhone = (value) => {
        if (!value) return value;
        const phoneNumber = value.replace(/[^\d]/g, '');
        const phoneNumberLength = phoneNumber.length;
        if (phoneNumberLength < 3) return phoneNumber;
        if (phoneNumberLength < 7) {
            return `(${phoneNumber.slice(0, 2)}) ${phoneNumber.slice(2)}`;
        }
        return `(${phoneNumber.slice(0, 2)}) ${phoneNumber.slice(2, 7)}-${phoneNumber.slice(7, 11)}`;
    };

    const handlePhoneChange = (e) => {
        const formattedValue = formatPhone(e.target.value);
        setWhatsapp(formattedValue);
    };

    const handleGoogleLogin = async () => {
        setIsSubmitting(true);
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const fbUser = result.user;

            // Check if user exists in Firestore
            const userDoc = await getDoc(doc(db, "users", fbUser.uid));

            if (view === 'login' && !userDoc.exists()) {
                showAlert("Você ainda não possui uma conta. Por favor, acesse a aba 'Criar Conta' e utilize seu Google para se cadastrar.", 'warning');
                setView('register');
                return;
            }



            if (!userDoc.exists() || !userDoc.data().whatsapp) {
                // We need the WhatsApp number
                setRegisteredName(fbUser.displayName || '');
                setEmail(fbUser.email || '');
                setView('collect_whatsapp');
            } else {
                setRegisteredName(userDoc.data().name || fbUser.displayName);
                setView('success');
                setTimeout(() => {
                    onLogin({
                        email: fbUser.email,
                        name: userDoc.data().name || fbUser.displayName,
                        whatsapp: userDoc.data().whatsapp
                    });
                }, 2500);
            }
        } catch (error) {
            console.error("🔴 ERRO GOOGLE LOGIN:", error);

            if (error.code === 'auth/cancelled-popup-request' || error.code === 'auth/popup-closed-by-user') {
                return;
            }

            if (error.code === 'auth/account-exists-with-different-credential') {
                // O e-mail já existe com outro provedor (provavelmente e-mail/senha)
                const pendingCredential = GoogleAuthProvider.credentialFromError(error);
                setPendingCred(pendingCredential);
                setEmail(error.customData.email);
                setView('login'); // Volta para o login para pedir a senha
                showAlert("Este e-mail já está em uso. Por favor, digite sua senha para vincular sua conta do Google.", "warning");
                return;
            }

            let msg = "Erro ao entrar com Google.";
            if (error.code === 'auth/operation-not-allowed') {
                msg = "O Login com Google não está ativado no seu Console Firebase.";
            } else if (error.code === 'auth/unauthorized-domain') {
                msg = "Este domínio não está autorizado no Console Firebase.";
            } else if (error.code === 'auth/credential-already-in-use') {
                msg = "Esta credencial já está associada a outra conta.";
            }

            showAlert(`${msg}\n\nDetalhe técnico: ${error.code}`, "danger");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleWhatsAppSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const fbUser = auth.currentUser;
            if (!fbUser) return;

            await setDoc(doc(db, "users", fbUser.uid), {
                name: registeredName || fbUser.displayName || email.split('@')[0],
                email: fbUser.email,
                whatsapp,
                role: 'client',
                createdAt: new Date().toISOString(),
                provider: 'google'
            }, { merge: true });

            setView('success');
            setTimeout(() => {
                onLogin({
                    email: fbUser.email,
                    name: registeredName || fbUser.displayName,
                    whatsapp
                });
            }, 2500);
        } catch (error) {
            console.error("Erro ao salvar Whatsapp:", error);
            showAlert("Erro ao salvar seus dados.", "danger");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (!name || !email || !password || !whatsapp) {
            showAlert("Por favor, preencha todos os campos.", "warning");
            return;
        }
        setIsSubmitting(true);

        try {
            // Se o usuário já está logado (ex: via Google) e quer adicionar senha
            if (auth.currentUser) {
                const credential = EmailAuthProvider.credential(email, password);
                await linkWithCredential(auth.currentUser, credential);

                // Atualizar dados no Firestore
                await setDoc(doc(db, "users", auth.currentUser.uid), {
                    name,
                    email,
                    whatsapp,
                    provider: 'linked'
                }, { merge: true });

                setRegisteredName(name);
                setView('success');
                setTimeout(() => {
                    onLogin({ email, name, whatsapp });
                }, 2500);
                return;
            }

            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const fbUser = userCredential.user;

            await updateProfile(fbUser, { displayName: name });

            await setDoc(doc(db, "users", fbUser.uid), {
                name,
                email,
                whatsapp,
                role: 'client',
                createdAt: new Date().toISOString(),
                provider: 'email'
            });

            setRegisteredName(name);
            setView('success');
            setTimeout(() => {
                onLogin({ email, name, whatsapp });
            }, 2500);
        } catch (error) {
            console.error("Erro no cadastro:", error);
            let message = "Erro ao criar conta.";
            if (error.code === 'auth/email-already-in-use') message = "Este e-mail já está em uso.";
            if (error.code === 'auth/weak-password') message = "A senha deve ter pelo menos 6 caracteres.";
            if (error.code === 'auth/invalid-email') message = "E-mail inválido.";
            if (error.code === 'auth/credential-already-in-use') message = "Esta senha já está vinculada a outra conta.";
            showAlert(message, 'danger');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            if (pendingCred) {
                // Se temos uma credencial do Google pendente, fazemos login com e-mail e vinculamos
                const userCredential = await signInWithEmailAndPassword(auth, email, password);
                await linkWithCredential(userCredential.user, pendingCred);
                setPendingCred(null);

                // Buscar dados do usuário no Firestore após vincular
                const userDoc = await getDoc(doc(db, "users", userCredential.user.uid));
                if (userDoc.exists()) {
                    onLogin({ email, name: userDoc.data().name, whatsapp: userDoc.data().whatsapp });
                } else {
                    onLogin({ email });
                }
            } else {
                // Firebase Login normal
                await signInWithEmailAndPassword(auth, email, password);
                onLogin({ email });
            }
        } catch (error) {
            console.error("Erro na autenticação:", error);
            let message = "Ocorreu um erro. Verifique seus dados.";

            if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential')
                message = "E-mail ou senha incorretos.";
            else if (error.code === 'auth/user-not-found')
                message = "Usuário não encontrado.";
            else if (error.code === 'auth/invalid-email')
                message = "E-mail inválido.";
            else if (error.code === 'auth/too-many-requests')
                message = "Muitas tentativas falhas. Tente novamente mais tarde.";
            else if (error.code === 'auth/credential-already-in-use')
                message = "Esta credencial já está vinculada a outra conta.";

            showAlert(`${message} (Código: ${error.code})`, "danger");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!email) {
            showAlert("Por favor, digite seu e-mail.", "warning");
            return;
        }

        console.log("📨 Solicitando reset de senha para:", email);
        setIsSubmitting(true);
        try {
            await sendPasswordResetEmail(auth, email);
            console.log("✅ E-mail de reset enviado com sucesso via Firebase.");
            setView('forgot_success');
        } catch (error) {
            console.error("❌ Erro detalhado no Reset:", error);
            let message = "Erro ao enviar e-mail.";
            if (error.code === 'auth/user-not-found') message = "Este e-mail não está cadastrado.";
            if (error.code === 'auth/invalid-email') message = "E-mail inválido.";
            if (error.code === 'auth/too-many-requests') message = "Muitas solicitações. Tente novamente mais tarde.";
            showAlert(message + " (Erro: " + error.code + ")", 'danger');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="login-overlay">
            <div className="login-modal">
                <button className="close-modal" onClick={onClose}>&times;</button>

                {view === 'success' ? (
                    <div className="login-success-view fade-in">
                        <div className="success-icon-wrapper">
                            <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="var(--gold-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                        </div>
                        <h2 className="login-title">Bem-vindo à Experiência</h2>
                        <p className="login-subtitle">Olá, {registeredName}. Sua jornada de estilo e luxo começa agora.</p>
                        <div className="loading-bar-container">
                            <div className="loading-bar-progress"></div>
                        </div>
                        <p className="redirect-text">Sincronizando seu perfil...</p>
                    </div>
                ) : view === 'forgot_success' ? (
                    <div className="login-success-view fade-in">
                        <div className="success-icon-wrapper">
                            <svg xmlns="http://www.w3.org/2000/svg" width="70" height="70" viewBox="0 0 24 24" fill="none" stroke="var(--gold-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                        </div>
                        <h2 className="login-title">Recuperação Iniciada</h2>
                        <p className="login-subtitle">
                            Instruções enviadas para o e-mail: <br />
                            <strong>{email}</strong>
                        </p>
                        <div className="login-info-box">
                            <p>✓ Verifique sua caixa de entrada</p>
                            <p>✓ Cheque a pasta de <strong>SPAM</strong></p>
                            <p>✓ O link expira em 1 hora</p>
                        </div>
                        <button className="login-submit-btn" onClick={() => setView('login')}>
                            VOLTAR AO LOGIN
                        </button>
                    </div>
                ) : view === 'collect_whatsapp' ? (
                    <div className="fade-in">
                        <h2 className="login-title">Só mais um detalhe...</h2>
                        <p className="login-subtitle">Para agendar, precisamos do seu WhatsApp</p>
                        <form onSubmit={handleWhatsAppSubmit} className="login-form">
                            <div className="form-group">
                                <label>Seu WhatsApp</label>
                                <input
                                    type="tel"
                                    value={whatsapp}
                                    onChange={handlePhoneChange}
                                    placeholder="(34) 99999-9999"
                                    maxLength="15"
                                    required
                                    autoFocus
                                />
                            </div>
                            <button type="submit" className="login-submit-btn" disabled={isSubmitting}>
                                {isSubmitting ? 'SALVANDO...' : 'CONCLUIR CADASTRO'}
                            </button>
                        </form>
                    </div>
                ) : (
                    <>
                        <h2 className="login-title">
                            {view === 'forgot' ? 'Recuperar Senha' :
                                view === 'register' ? 'Criar Conta' : 'Acessar Conta'}
                        </h2>
                        <p className="login-subtitle">
                            {view === 'forgot'
                                ? 'Enviaremos um link para o seu e-mail'
                                : view === 'register' ? 'Cadastre-se para agendar' : 'Portal de Agendamento'}
                        </p>

                        {(view === 'login' || view === 'register') && (
                            <div className="social-login-area">
                                <button className="google-login-btn" onClick={handleGoogleLogin} disabled={isSubmitting}>
                                    <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                    </svg>
                                    {view === 'register' ? 'Cadastrar com Google' : 'Entrar com Google'}
                                </button>
                                <div className="login-divider">
                                    <span>ou use seu e-mail</span>
                                </div>
                            </div>
                        )}

                        <form onSubmit={view === 'forgot' ? handleResetPassword :
                            view === 'register' ? handleRegister : handleSubmit}
                            className="login-form">

                            {view === 'register' && (
                                <div className="form-group slide-down">
                                    <label>Seu Nome (Nome de Pessoa)</label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Ex: João Silva"
                                        required
                                    />
                                </div>
                            )}

                            <div className="form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="seu@email.com"
                                    required
                                />
                            </div>

                            {view !== 'forgot' && (
                                <div className="form-group password-group">
                                    <label>Senha</label>
                                    <div className="password-input-wrapper">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="********"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                            tabIndex="-1"
                                            aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
                                        >
                                            {showPassword ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                            )}
                                        </button>
                                    </div>
                                    {view === 'login' && (
                                        <button type="button" className="forgot-password-link" onClick={() => setView('forgot')}>
                                            Esqueci minha senha
                                        </button>
                                    )}
                                </div>
                            )}

                            {view === 'register' && (
                                <div className="form-group slide-down">
                                    <label>WhatsApp</label>
                                    <input
                                        type="tel"
                                        value={whatsapp}
                                        onChange={handlePhoneChange}
                                        placeholder="(34) 99999-9999"
                                        maxLength="15"
                                        required
                                    />
                                </div>
                            )}

                            <button type="submit" className="login-submit-btn" disabled={isSubmitting}>
                                {isSubmitting ? 'AGUARDE...' :
                                    (view === 'forgot' ? 'ENVIAR LINK' :
                                        view === 'register' ? 'CRIAR MINHA CONTA' : 'ENTRAR NA CONTA')}
                            </button>
                        </form>

                        <div className="login-footer">
                            <p>
                                {view === 'register' ? 'Já tem uma conta?' :
                                    view === 'forgot' ? 'Lembrou a senha?' : 'Ainda não tem conta?'}
                                <button className="auth-switch-btn" onClick={() => {
                                    setView(view === 'register' || view === 'forgot' ? 'login' : 'register');
                                    setWhatsapp('');
                                    setName('');
                                }}>
                                    {view === 'register' || view === 'forgot' ? 'Faça Login' : 'Cadastre-se'}
                                </button>
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Acesso;
